import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffClassService, ParticularClassDetail } from '../services/staff-class.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

@Component({
  selector: 'app-staff-class-overview',
  templateUrl: './staff-class-overview.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-overview.component.css']
})
export class StaffClassOverviewComponent implements OnInit {
  classId: number | string;
  className = '';
  section = '';

  // Real values from the API.
  totalStudents = 0;
  presentCount = 0;
  absentCount = 0;
  lateCount = 0;
  leaveCount = 0;
  attendancePercent = 0;

  // Values actually shown in the template — animated up to the real value
  // above whenever fresh data arrives, so the numbers count up visually.
  displayTotalStudents = 0;
  displayPresentCount = 0;
  displayAbsentCount = 0;
  displayLateCount = 0;
  displayLeaveCount = 0;
  displayAttendancePercent = 0;

  currentMonthLabel = '';
  classTeacherName = '';
  attendanceType = '';

  loading = true;
  loadError = false;

  tiles = [
    { key: 'diary', label: 'diary', subtitle: 'notesAndHomework', icon: 'ri-file-list-line', color: 'blue', feature: 'DIGITAL_DIARY' },
    { key: 'attendance', label: 'attendanceLabel', subtitle: 'markDaily', icon: 'ri-checkbox-line', color: 'indigo', feature: 'ATTENDANCE' },
    { key: 'timetable', label: 'timetable', subtitle: 'classSchedule', icon: 'ri-calendar-line', color: 'green', feature: 'TIME_TABLE_MANAGEMENT' },
    { key: 'activity', label: 'activityLabel', subtitle: 'trackEvents', icon: 'ri-zoom-in-line', color: 'pink', feature: 'GET_ACTIVITY_TIMELINE' },
    { key: 'chat', label: 'chatLabel', subtitle: 'messageParents', icon: 'ri-chat-3-line', color: 'purple', feature: 'STAFF_PARENT_CHAT' },
  ];

  get visibleTiles() {
    return this.tiles.filter(t => this.staffSession.isFeatureEnabled(t.feature));
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffClassService: StaffClassService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id') || '';
    this.currentMonthLabel = new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    this.loadClassInfo();
  }

  async loadClassInfo() {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails || !this.classId) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    // Fall back to the cached card data (from the dashboard tap) for an
    // instant paint while the fresh particular-class-info call resolves.
    const cached = this.staffSession.getClassData();
    if (cached) {
      this.applyCachedClassCard(cached);
    }

    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffClassService.getParticularClassInfo(staffDetails.staffId, this.classId)
      );
      const responseDetails = response as any;
      const classDetails: ParticularClassDetail[] = responseDetails.body?.classDetails || [];
      if (responseDetails.status && classDetails.length) {
        this.applyClassDetail(classDetails[0]);
      } else if (!cached) {
        this.loadError = true;
        safeToast(await firstValueFrom(this.translate.get('couldNotLoadClassTryAgain')), 'error');
      }
    } catch (error) {
      if (!cached) {
        this.loadError = true;
        safeToast(await firstValueFrom(this.translate.get('couldNotLoadClassTryAgain')), 'error');
      }
    } finally {
      this.loading = false;
    }
  }

  private applyCachedClassCard(c: any) {
    this.className = c.classNumber || this.className;
    this.section = c.classDivision || this.section;
    this.classTeacherName = c.classTeacherName || this.classTeacherName;
  }

  private applyClassDetail(c: ParticularClassDetail) {
    this.totalStudents = c.studentCount ?? this.totalStudents;
    this.presentCount = c.presentCount ?? 0;
    this.absentCount = c.absentCount ?? 0;
    this.lateCount = c.lateCount ?? 0;
    this.leaveCount = c.leaveCount ?? 0;
    this.attendancePercent = Math.round(c.attendancePercentage ?? 0);
    this.attendanceType = c.attendanceType || this.attendanceType;
    this.animateStatsIn();
  }

  // Counts every stat up from 0 to its real value over ~800ms so the hero
  // card's numbers visibly tick up instead of just appearing.
  private animateStatsIn() {
    const duration = 800;
    const start = performance.now();
    const targets = {
      displayTotalStudents: this.totalStudents,
      displayPresentCount: this.presentCount,
      displayAbsentCount: this.absentCount,
      displayLateCount: this.lateCount,
      displayLeaveCount: this.leaveCount,
      displayAttendancePercent: this.attendancePercent,
    } as const;

    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      (Object.keys(targets) as (keyof typeof targets)[]).forEach(key => {
        this[key] = Math.round(targets[key] * eased);
      });
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }

  goBack() {
    this.router.navigate(['/staff/classes']);
  }

  // "Attendance" is the one tile whose destination depends on the class's
  // own attendanceType: DAY-based classes go straight to the day-wise
  // attendance screen, everything else (period-based) goes to the period
  // picker first so staff can choose which period to mark.
  openTile(tileKey: string) {
    if (tileKey === 'attendance' && this.attendanceType && this.attendanceType !== 'DAY') {
      this.router.navigate(['/staff/class', this.classId, 'period-wise-attendance']);
      return;
    }
    this.router.navigate(['/staff/class', this.classId, tileKey]);
  }

  openStudentsList() {
    this.router.navigate(['/staff/class', this.classId, 'students']);
  }
}
